import { useCallback, useEffect, useRef, useState } from 'react';
import { streamProwl } from './prowlStreamClient';
import { saveResumeSnapshot } from './prowlResumeStorage';
import type {
  ActionKey,
  ComponentType,
  Persona,
  ProwlPageContext,
  ProwlResultSetData,
  ToolStatus,
} from './contracts';

export interface ProwlToolActivity {
  toolCallId: string;
  name: string;
  label: string;
  status: 'running' | ToolStatus;
  durationMs?: number;
}

export interface ProwlComponentEvent {
  componentId: string;
  type: ComponentType | string;
  props: unknown;
  actionKey?: ActionKey | string;
  actionPayload?: unknown;
}

export interface ProwlMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  streaming: boolean;
  done: boolean;
  activity: ProwlToolActivity[];
  components: ProwlComponentEvent[];
  escalation?: { ref: string; channel: 'email' } | null;
  error?: { code: string; message: string; retryable: boolean } | null;
}

let nextId = 1;
const genId = () => `${Date.now()}-${nextId++}`;

const GREETING_TEXT =
  "Hi! I'm your AI assistant — ask me anything about HirePanther in your own words, no need for exact phrasing.";

const makeGreeting = (): ProwlMessage => ({
  id: genId(),
  role: 'assistant',
  text: GREETING_TEXT,
  streaming: false,
  done: true,
  activity: [],
  components: [],
});

export interface UseProwlConversationOptions {
  persona: Persona;
}

/**
 * Owns one Prowl conversation: turn accumulation over the SSE wire protocol
 * (design-spec §7). Replaces the two-call faq/ai-search + conciergeSearch
 * pattern (task requirement 1) and the cosmetic character-reveal timer
 * (requirement 2) with real token deltas rendered as they arrive.
 */
export function useProwlConversation({ persona }: UseProwlConversationOptions) {
  const [messages, setMessages] = useState<ProwlMessage[]>([makeGreeting()]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [requiresSignIn, setRequiresSignIn] = useState(false);
  // One search, one grid (prowl-overnight-batch.md): the services results
  // page renders THIS instead of running its own query while a Prowl turn is
  // active for it. Held back from `resultSet` until `done` fires (design-spec
  // 1g#2: "grid shows the previous result set at 55% opacity until done, then
  // swaps" — avoids the grid flashing mid-stream while text keeps arriving).
  const [resultSet, setResultSet] = useState<ProwlResultSetData | null>(null);
  const pendingResultSetRef = useRef<ProwlResultSetData | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const updateAssistant = useCallback(
    (id: string, updater: (m: ProwlMessage) => ProwlMessage) => {
      setMessages((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
    },
    []
  );

  const send = useCallback(
    async (text: string, pageContext?: ProwlPageContext) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      pendingResultSetRef.current = null;

      const userMsg: ProwlMessage = {
        id: genId(),
        role: 'user',
        text: trimmed,
        streaming: false,
        done: true,
        activity: [],
        components: [],
      };
      const assistantId = genId();
      const assistantMsg: ProwlMessage = {
        id: assistantId,
        role: 'assistant',
        text: '',
        streaming: true,
        done: false,
        activity: [],
        components: [],
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);
      setRequiresSignIn(false);

      try {
        await streamProwl(
          {
            sessionId: sessionIdRef.current,
            message: trimmed,
            persona,
            pageContext,
          },
          {
            signal: controller.signal,
            onHttpError: (status) => {
              if (status === 401 || status === 403) setRequiresSignIn(true);
            },
            onEvent: (evt) => {
              switch (evt.event) {
                case 'session':
                  setSessionId(evt.data.sessionId);
                  break;

                case 'text_delta':
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    text: m.text + evt.data.text,
                  }));
                  break;

                case 'tool_started':
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    activity: [
                      ...m.activity,
                      {
                        toolCallId: evt.data.toolCallId,
                        name: evt.data.name,
                        label: evt.data.label,
                        status: 'running',
                      },
                    ],
                  }));
                  break;

                case 'tool_finished':
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    activity: m.activity.map((a) =>
                      a.toolCallId === evt.data.toolCallId
                        ? {
                            ...a,
                            status: evt.data.status,
                            durationMs: evt.data.durationMs,
                          }
                        : a
                    ),
                  }));
                  break;

                case 'component':
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    components: [
                      ...m.components,
                      {
                        componentId: evt.data.componentId,
                        type: evt.data.type,
                        props: evt.data.props,
                      },
                    ],
                  }));
                  break;

                case 'confirm_request':
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    components: m.components.map((c) =>
                      c.componentId === evt.data.componentId
                        ? {
                            ...c,
                            actionKey: evt.data.actionKey,
                            actionPayload: evt.data.payload,
                          }
                        : c
                    ),
                  }));
                  break;

                case 'escalation':
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    escalation: evt.data,
                  }));
                  break;

                case 'result_set':
                  pendingResultSetRef.current = evt.data;
                  break;

                case 'error':
                  if (
                    evt.data.code === 'unauthorized' ||
                    evt.data.code === 'feature_disabled'
                  ) {
                    setRequiresSignIn(evt.data.code === 'unauthorized');
                  }
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    error: evt.data,
                  }));
                  break;

                case 'done':
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    streaming: false,
                    done: true,
                  }));
                  if (pendingResultSetRef.current) {
                    const landed = pendingResultSetRef.current;
                    setResultSet(landed);
                    pendingResultSetRef.current = null;
                    // design-spec 3d: this is the fact a later visit's
                    // resume card reports. Only worth remembering when the
                    // turn actually found something — an empty/thin result
                    // isn't "still looking at X", it's nothing to resume.
                    if (landed.total > 0) {
                      saveResumeSnapshot({
                        categoryName: landed.interpretedQuery.categoryName,
                        city: landed.interpretedQuery.city,
                        providerCount: landed.total,
                      });
                    }
                  }
                  break;

                default:
                  break;
              }
            },
          }
        );
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        updateAssistant(assistantId, (m) => ({
          ...m,
          streaming: false,
          done: true,
          error: {
            code: 'network_error',
            message:
              "I'm having trouble connecting. Please try again in a moment.",
            retryable: true,
          },
        }));
      } finally {
        setIsStreaming(false);
      }
    },
    [isStreaming, persona, updateAssistant]
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    pendingResultSetRef.current = null;
    setMessages([makeGreeting()]);
    setSessionId(null);
    setIsStreaming(false);
    setRequiresSignIn(false);
    setResultSet(null);
  }, []);

  return {
    messages,
    sessionId,
    isStreaming,
    requiresSignIn,
    resultSet,
    send,
    reset,
  };
}

export type UseProwlConversationReturn = ReturnType<
  typeof useProwlConversation
>;
